import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getImageById } from '../../services/apiService';
import './ShareImagePage.css';

const ShareImagePage = () => {
  const { imageId } = useParams();
  const navigate = useNavigate();
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to update meta tags
  const updateMetaTag = (property, content) => {
    let meta = document.querySelector(`meta[property="${property}"]`) || 
               document.querySelector(`meta[name="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      // Use 'property' for Open Graph, 'name' for others
      if (property.startsWith('og:') || property.startsWith('twitter:')) {
        meta.setAttribute('property', property);
      } else {
        meta.setAttribute('name', property);
      }
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  // Set meta tags immediately when component mounts (before image loads)
  // This ensures Facebook's crawler sees them even if it doesn't wait for async operations
  useEffect(() => {
    const shareLink = `${window.location.origin}/share/image/${imageId}`;
    const title = 'Check out this image from KURA';
    const description = 'Transform your ideas into stunning visuals with AI-powered image generation. Try it yourself and see what you can create!';
    
    // Set basic meta tags immediately
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', shareLink);
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:site_name', 'KURA');
    updateMetaTag('og:locale', 'en_US');
    updateMetaTag('description', description);
    document.title = title;

    // Set placeholder image meta tags (will be updated when image loads)
    const placeholderImage = `${window.location.origin}/logo.png`; // Fallback to logo
    updateMetaTag('og:image', placeholderImage);
    updateMetaTag('og:image:secure_url', placeholderImage);
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', placeholderImage);

    // Cleanup on unmount
    return () => {
      document.title = 'Hey KURA';
    };
  }, [imageId]);

  // Update meta tags with actual image data when it loads
  useEffect(() => {
    if (imageData && imageData.url) {
      // Ensure image URL is absolute
      const imageUrl = imageData.url.startsWith('http') 
        ? imageData.url 
        : `${window.location.origin}${imageData.url}`;
      
      // Detect image type from URL
      const getImageType = (url) => {
        const extension = url.split('.').pop()?.toLowerCase();
        const typeMap = {
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'webp': 'image/webp'
        };
        return typeMap[extension] || 'image/png';
      };

      // Update image-specific meta tags
      updateMetaTag('og:image', imageUrl);
      updateMetaTag('og:image:secure_url', imageUrl);
      updateMetaTag('og:image:type', getImageType(imageUrl));
      updateMetaTag('og:image:width', '1200');
      updateMetaTag('og:image:height', '630');
      updateMetaTag('twitter:image', imageUrl);
    }
  }, [imageData, imageId]);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!imageId) {
          setError('Image ID is required');
          setLoading(false);
          return;
        }

        const response = await getImageById(imageId);
        
        if (response.success && response.data) {
          const imageData = response.data;
          setImageData(imageData);
          
          // Immediately update meta tags with actual image URL
          // This ensures Facebook's crawler sees the correct image
          if (imageData.url) {
            const imageUrl = imageData.url.startsWith('http') 
              ? imageData.url 
              : `${window.location.origin}${imageData.url}`;
            
            const getImageType = (url) => {
              const extension = url.split('.').pop()?.toLowerCase();
              const typeMap = {
                'jpg': 'image/jpeg',
                'jpeg': 'image/jpeg',
                'png': 'image/png',
                'gif': 'image/gif',
                'webp': 'image/webp'
              };
              return typeMap[extension] || 'image/png';
            };

            // Update image meta tags immediately
            updateMetaTag('og:image', imageUrl);
            updateMetaTag('og:image:secure_url', imageUrl);
            updateMetaTag('og:image:type', getImageType(imageUrl));
            updateMetaTag('og:image:width', '1200');
            updateMetaTag('og:image:height', '630');
            updateMetaTag('twitter:image', imageUrl);
            
            // Log for debugging
            console.log('[ShareImagePage] Meta tags updated with image URL:', imageUrl);
          }
        } else {
          setError('Image not found');
        }
      } catch (err) {
        console.error('Error fetching image:', err);
        setError(err.message || 'Failed to load image');
      } finally {
        setLoading(false);
      }
    };

    if (imageId) {
      fetchImage();
    }
  }, [imageId]);

  const handleClose = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="share-image-page">
        <div className="share-image-loading">Loading image...</div>
      </div>
    );
  }

  if (error || !imageData) {
    return (
      <div className="share-image-page">
        <div className="share-image-error">
          {error || 'Image not found'}
          <button onClick={handleClose} style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="share-image-page">
        <div className="share-image-container">
          <div className="share-image-wrapper">
            <img 
              src={imageData.url} 
              alt="Shared from KURA" 
              className="share-image-full"
            />
          </div>
          <div className="share-image-content">
            <h2 className="share-image-title">Check out this image from KURA</h2>
            <p className="share-image-description">
              Transform your ideas into stunning visuals with AI-powered image generation. 
              Try it yourself and see what you can create!
            </p>
            <button 
              className="share-image-button"
              onClick={() => navigate('/')}
            >
              Try It Yourself →
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareImagePage;

